import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Badge,
  Button,
  Card,
  Collapse,
  List,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { useActiveOrg } from '@/features/organizations/active-org-context';
import { approvalsApi } from '@/features/approvals/approvals.api';
import { agentsApi, type AgentRun, type AgentTask } from '../agents.api';

const STATUS_COLORS: Record<string, string> = {
  queued: 'blue',
  running: 'geekblue',
  awaiting_approval: 'gold',
  succeeded: 'green',
  failed: 'red',
  cancelled: 'default',
};

export function AgentsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { activeOrg, activeOrgId } = useActiveOrg();
  const isAdmin = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';
  const orgId = activeOrgId ?? '';
  const enabled = Boolean(orgId);

  const agentsQuery = useQuery({
    queryKey: ['agents', orgId],
    queryFn: () => agentsApi.list(orgId),
    enabled,
  });
  // Poll fast while anything is active so the queue and logs feel live, then
  // back off to a slow heartbeat when idle.
  const tasksQuery = useQuery({
    queryKey: ['agent-tasks', orgId],
    queryFn: () => agentsApi.listTasks(orgId),
    enabled,
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((t) =>
        ['queued', 'running', 'awaiting_approval'].includes(t.status),
      )
        ? 4000
        : 20000,
  });
  const runsQuery = useQuery({
    queryKey: ['agent-runs', orgId],
    queryFn: () => agentsApi.listRuns(orgId),
    enabled,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === 'running') ? 4000 : 20000,
  });
  const approvalsQuery = useQuery({
    queryKey: ['approvals', orgId],
    queryFn: () => approvalsApi.listPending(orgId),
    enabled: enabled && isAdmin,
  });

  const refetchAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['agent-tasks', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['agent-runs', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['approvals', orgId] });
  };

  const enqueue = useMutation({
    mutationFn: (kind: string) => agentsApi.enqueue(orgId, { agentType: 'system', kind }),
    onSuccess: () => {
      refetchAll();
      message.success('Task queued — the worker will run it shortly');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to queue task'),
  });

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      action === 'approve' ? approvalsApi.approve(orgId, id) : approvalsApi.reject(orgId, id),
    onSuccess: () => {
      refetchAll();
      message.success('Decision recorded');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to record decision'),
  });

  if (!activeOrg) return <Typography.Paragraph>Select an organization first.</Typography.Paragraph>;

  const agentsTab = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <List
        grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
        dataSource={agentsQuery.data ?? []}
        renderItem={(a) => (
          <List.Item>
            <Card size="small" title={`${a.type} · ${a.kind}`}>
              <Typography.Paragraph type="secondary">{a.description}</Typography.Paragraph>
            </Card>
          </List.Item>
        )}
      />
      {isAdmin ? (
        <Space>
          <Button onClick={() => enqueue.mutate('heartbeat')} loading={enqueue.isPending}>
            Run heartbeat task
          </Button>
          <Button onClick={() => enqueue.mutate('approval_demo')} loading={enqueue.isPending}>
            Run approval demo
          </Button>
        </Space>
      ) : null}
    </Space>
  );

  const activityTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="Recent tasks">
        <Table<AgentTask>
          rowKey="id"
          size="small"
          loading={tasksQuery.isLoading}
          dataSource={tasksQuery.data?.items ?? []}
          pagination={false}
          columns={[
            { title: 'Agent', dataIndex: 'agentType' },
            { title: 'Kind', dataIndex: 'kind' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (s: string) => <Tag color={STATUS_COLORS[s] ?? 'default'}>{s}</Tag>,
            },
            { title: 'Error', dataIndex: 'error', render: (e: string | null) => e ?? '—' },
          ]}
        />
      </Card>
      <Card size="small" title="Run logs">
        {(runsQuery.data ?? []).length === 0 ? (
          <Typography.Text type="secondary">No runs yet</Typography.Text>
        ) : (
          <Collapse
            // Auto-expand live runs so their logs tail without a click.
            defaultActiveKey={(runsQuery.data ?? [])
              .filter((r) => r.status === 'running')
              .map((r) => r.id)}
            items={(runsQuery.data ?? []).map((r: AgentRun) => ({
              key: r.id,
              label: (
                <Space>
                  {r.status === 'running' ? <Badge status="processing" /> : null}
                  <Typography.Text strong>
                    {r.agentType} · {r.kind}
                  </Typography.Text>
                  <Tag color={STATUS_COLORS[r.status] ?? 'default'}>{r.status}</Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(r.startedAt).toLocaleString()}
                  </Typography.Text>
                </Space>
              ),
              children: (
                <>
                  <Timeline
                    items={r.steps.map((s) => ({
                      color:
                        s.phase === 'plan'
                          ? 'blue'
                          : s.phase === 'execute'
                            ? 'green'
                            : s.phase === 'evaluate'
                              ? 'orange'
                              : 'purple',
                      children: (
                        <Space direction="vertical" size={0}>
                          <Space size={6}>
                            <Tag style={{ margin: 0 }}>{s.phase}</Tag>
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                              {new Date(s.at).toLocaleTimeString()}
                            </Typography.Text>
                          </Space>
                          <Typography.Text>{s.detail}</Typography.Text>
                        </Space>
                      ),
                    }))}
                  />
                  {r.error ? <Typography.Text type="danger">{r.error}</Typography.Text> : null}
                </>
              ),
            }))}
          />
        )}
      </Card>
    </Space>
  );

  const approvalsTab = (
    <List
      loading={approvalsQuery.isLoading}
      dataSource={approvalsQuery.data ?? []}
      locale={{ emptyText: 'No pending approvals' }}
      renderItem={(a) => (
        <List.Item
          actions={[
            <Button
              key="approve"
              type="primary"
              size="small"
              onClick={() => decide.mutate({ id: a.id, action: 'approve' })}
            >
              Approve
            </Button>,
            <Popconfirm
              key="reject"
              title="Reject this request?"
              onConfirm={() => decide.mutate({ id: a.id, action: 'reject' })}
            >
              <Button size="small" danger>
                Reject
              </Button>
            </Popconfirm>,
          ]}
        >
          <List.Item.Meta title={a.title} description={a.summary} />
        </List.Item>
      )}
    />
  );

  const tabs = [
    { key: 'agents', label: 'Agents', children: agentsTab },
    { key: 'activity', label: 'Activity', children: activityTab },
    ...(isAdmin ? [{ key: 'approvals', label: 'Approvals', children: approvalsTab }] : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {activeOrg.name} · Agents
      </Typography.Title>
      <Tabs defaultActiveKey="agents" items={tabs} />
    </Space>
  );
}
